import { LitElement, html, css } from 'lit';

import {
  CSS_FONT_SIZE_HEADER,
  CSS_FONT_WEIGHT_BOLD,
  CSS_SPACING,
} from '../../../neb-styles/neb-variables';
import { parseDate } from '../../../neb-utils/date-util';
import { formatDuration } from '../../../neb-utils/formatters';

export const ELEMENTS = {
  serviceInfo: { id: 'service-info' },
  dateAndTime: { id: 'date-and-time' },
  provider: { id: 'provider' },
  address1: { id: 'address' },
  cityStateZipCode: { id: 'city-state-zip-code' },
  address2: { id: 'address2' },
  locationName: { id: 'location-name' },
};
class NebBookingAppointmentDetails extends LitElement {
  static get properties() {
    return {
      appointment: Object,
      layout: {
        type: String,
        reflect: true,
      },
      provider: Object,
      location: Object,
    };
  }

  static get styles() {
    return css`
      :host {
        display: flex;
        flex-direction: column;
        font-size: ${CSS_FONT_SIZE_HEADER};
        font-weight: ${CSS_FONT_WEIGHT_BOLD};
      }

      :host([layout='small']) .container-content {
        display: flex;
        flex-direction: column;
      }

      .container-content {
        display: grid;
        grid-template-columns: 1fr 1fr;
        margin-top: ${CSS_SPACING};
        font-weight: normal;
      }
      .address {
        display: flex;
        flex-direction: column;
        text-decoration: underline;
        cursor: pointer;
      }
      .location-name {
        display: flex;
        flex-direction: column;
        font-weight: ${CSS_FONT_WEIGHT_BOLD};
      }
    `;
  }

  constructor() {
    super();

    this.__initState();
    this.__initHandlers();
  }

  __initState() {
    this.appointment = {};
    this.layout = '';
    this.provider = {};
  }

  __initHandlers() {
    this.__handlers = {
      openMap: () => {
        const fullAddress = [
          this.location.address1,
          ...(this.location.address2 ? [this.location.address2] : []),
          this.location.city,
          this.location.state,
          this.location.zipCode,
        ].join(', ');

        const isApple =
          /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

        const toolUrl =
          (isApple
            ? 'http://maps.apple.com/?q='
            : 'https://www.google.com/maps/place/') + fullAddress;

        window.open(toolUrl, '_blank');
      },
    };
  }

  __getProviderDisplay() {
    return `${this.provider.lastName}, ${this.provider.firstName}`;
  }

  __getDurationDisplay() {
    if (
      this.appointment.duration ||
      (this.appointment.start && this.appointment.end)
    ) {
      const duration = this.appointment.duration
        ? this.appointment.duration
        : parseDate(this.appointment.end).diff(
            parseDate(this.appointment.start),
          );
      const durationTexts = formatDuration(duration);

      if (durationTexts.hours) {
        return durationTexts.minutes
          ? `${durationTexts.hours} ${durationTexts.minutes}`
          : `${durationTexts.hours}`;
      }
      return durationTexts.minutes;
    }

    return '';
  }

  __getServiceName() {
    return this.appointment.serviceName || this.appointment.appointmentTypeName;
  }

  __getLocationDisplay() {
    return this.location.active
      ? html`
          <div>
            <div class="location-name" id="${ELEMENTS.locationName.id}">
              ${this.location.name}
            </div>

            <a
              class="address"
              id="${ELEMENTS.address1.id}"
              @click="${this.__handlers.openMap}"
              >${this.location.address1}</a
            >

            <a
              class="address"
              id="${ELEMENTS.address2.id}"
              @click="${this.__handlers.openMap}"
              >${this.location.address2 ? this.location.address2 : ''}</a
            >

            <a
              class="address"
              id="${ELEMENTS.cityStateZipCode.id}"
              @click="${this.__handlers.openMap}"
              >${this.location.city}, ${this.location.state}
              ${this.location.zipCode}</a
            >
          </div>
        `
      : html`
          <div class="location-name" id="${ELEMENTS.locationName.id}">
            ${this.location.name}
          </div>
        `;
  }

  render() {
    return html`
      ${Object.keys(this.appointment).length > 0
        ? html`
            <span id="${ELEMENTS.serviceInfo.id}">
              ${this.__getServiceName()} (${this.__getDurationDisplay()})
            </span>
          `
        : ''}
      ${this.appointment.start
        ? html`
            <span id="${ELEMENTS.dateAndTime.id}"
              >${parseDate(this.appointment.start).format('dddd, MMMM D, YYYY')}
              at ${parseDate(this.appointment.start).format('h:mm A')}</span
            >
          `
        : ''}
      ${this.provider && this.provider.firstName
        ? html`
            <span id="${ELEMENTS.provider.id}"
              >${this.__getProviderDisplay()}</span
            >
          `
        : ''}
      ${this.location
        ? html`
            <div class="container-content">${this.__getLocationDisplay()}</div>
          `
        : ''}
    `;
  }
}

customElements.define(
  'neb-booking-appointment-details',
  NebBookingAppointmentDetails,
);
